
export interface RouteContext {
  path: string;
  params: Record<string, string>;
  query: URLSearchParams;
  navigate(to: string, opts?: { replace?: boolean }): void;
}

export interface RouteModule {
  render: (ctx: RouteContext) => void | Promise<void>;
}

export interface Guard {
  (ctx: RouteContext): string | null | Promise<string | null>;
}

export interface RouteDef {
  path: string;
  load: () => Promise<RouteModule>;
  guard?: Guard;
}

interface RouterConfig {
  mount: string;
  guards?: Guard[];
}

const PARAM_RE = /:([a-zA-Z_][a-zA-Z0-9_]*)/g;

function compile(pattern: string): { re: RegExp; keys: string[] } {
  const keys: string[] = [];
  const escaped = pattern.replace(/[.+^${}()|[\]\\]/g, "\\$&");
  const src = escaped.replace(PARAM_RE, (_m, name: string) => {
    keys.push(name);
    return "([^/]+)";
  });
  return { re: new RegExp("^" + src + "/?$"), keys };
}

function match(pattern: string, path: string): Record<string, string> | null {
  if (pattern === "*") return {};
  const { re, keys } = compile(pattern);
  const m = re.exec(path);
  if (!m) return null;
  const out: Record<string, string> = {};
  keys.forEach((k, i) => {
    out[k] = decodeURIComponent(m[i + 1] ?? "");
  });
  return out;
}

let currentRoutes: RouteDef[] = [];
let currentMount: HTMLElement | null = null;
let globalGuards: Guard[] = [];
let activeRenderToken = 0;

/** Émis sur `window` après chaque rendu de route (présence live, etc.). */
export const ROUTE_RENDERED_EVENT = "dropl:route-rendered";
function notifyRendered(): void {
  window.dispatchEvent(new CustomEvent(ROUTE_RENDERED_EVENT, { detail: { path: window.location.pathname } }));
}

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (opts.replace) window.history.replaceState({}, "", to);
  else window.history.pushState({}, "", to);
  void render();
}

function buildCtx(path: string, params: Record<string, string>): RouteContext {
  const [, qs = ""] = path.split("?");
  return {
    path,
    params,
    query: new URLSearchParams(qs),
    navigate,
  };
}

async function render(): Promise<void> {
  const token = ++activeRenderToken;
  if (!currentMount) return;
  const url = window.location.pathname + window.location.search;
  const pathOnly = window.location.pathname;

  for (const route of currentRoutes) {
    const params = match(route.path, pathOnly);
    if (!params) continue;

    const ctx = buildCtx(url, params);

    for (const g of globalGuards) {
      const redir = await g(ctx);
      if (token !== activeRenderToken) return;
      if (redir) return navigate(redir, { replace: true });
    }
    if (route.guard) {
      const redir = await route.guard(ctx);
      if (token !== activeRenderToken) return;
      if (redir) return navigate(redir, { replace: true });
    }

    try {
      const mod = await route.load();
      if (token !== activeRenderToken) return;
      currentMount.scrollTo?.({ top: 0 });
      await mod.render(ctx);
      if (token === activeRenderToken) notifyRendered();
    } catch (err: unknown) {
      if (token !== activeRenderToken) return;
      const msg = err instanceof Error ? err.message : String(err);
      if (/Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(msg)) {
        const FLAG = "dropl:chunk_reload";
        const last = Number(sessionStorage.getItem(FLAG) ?? 0);
        if (Date.now() - last > 10_000) {
          sessionStorage.setItem(FLAG, String(Date.now()));
          window.location.reload();
          return;
        }
      }
      currentMount.innerHTML = `<section class="section container-narrow"><h1>500</h1><p class="t-lead">${msg}</p></section>`;
    }
    return;
  }
  currentMount.innerHTML = `<section class="section container-narrow"><h1>404</h1></section>`;
  notifyRendered();
}

function interceptLinks(): void {
  document.addEventListener("click", (ev) => {
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    const a = target.closest?.("a[data-nav]") as HTMLAnchorElement | null;
    if (!a) return;
    const href = a.getAttribute("href");
    if (!href || href.startsWith("http") || href.startsWith("mailto:")) return;
    ev.preventDefault();
    navigate(href);
  });
}

export function defineRoutes(routes: RouteDef[], cfg: RouterConfig): void {
  currentRoutes = routes;
  globalGuards = cfg.guards ?? [];
  currentMount = document.querySelector(cfg.mount);
  if (!currentMount) throw new Error("router: mount element not found: " + cfg.mount);
  window.addEventListener("popstate", () => {
    void render();
  });
  interceptLinks();
  void render();
}
